/**
 * New tasks and things Denker learned, as wall cards (Part 09 decision 5):
 * made in the last 7 days, not yet opened (`lib/wall-new-opened.ts`), and
 * matching the wall's search.
 *
 * - Tasks: only on Everything, the latest tasks across projects
 *   (`GET /api/v1/search/?kinds=tasks`, empty query). A project wall has no
 *   task cards: its board tile already lists them by status.
 * - Things: the user's recent active things worth a card (a live fact or a
 *   description; a hidden one again only when it learns a new fact, and
 *   like every card only in its first 7 days), without the user profile,
 *   conversation summaries and things the user made (`origin: "explicit"`);
 *   only with the plan's memory. A project wall shows only what its runs
 *   taught Denker (`learned_in_project`), Everything all of it. A card says
 *   the newest fact's claim (on a project wall, the newest learned there),
 *   else the description.
 *
 * The remote sources are fetched ahead of the wall's first show (hidden),
 * when the scope or the project changes, and on a show when the last fetch
 * is over a minute old; no polling. A show joins a fetch in flight. The
 * wall opens once the fetch it shows has settled (`useWallNewItemsReady`),
 * so new cards never pop in after it, and another wall's things never show
 * on it. Archived shows none.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { create } from "zustand";
import { listWallThings, type KnowledgeNodeListItem } from "@/api/memory";
import type { TaskStatus } from "@/api/tasks";
import { searchWorkspace, type WorkspaceTask } from "@/api/workspace-search";
import { isLearnedThingType } from "@/lib/memory-thing-kinds";
import { sameIds } from "@/lib/same-ids";
import {
  useWallOpenedKeys,
  WALL_NEW_MS,
  type WallNewKey,
} from "@/lib/wall-new-opened";
import type { WallScope } from "@/stores/wall-scope-store";
import { wallFrame } from "./wall-frame-source";
import { appendNewItems, mergeNewItems } from "./wall-layout";

/** What a new task card shows and acts on. */
export interface WallNewTask {
  id: string;
  title: string;
  status: TaskStatus;
  ticketDisplay: string | null;
  assigneeName: string | null;
  projectId: string;
}

/** What a new memory card shows and opens. */
export interface WallNewThing {
  id: string;
  name: string;
  /** What Denker learned: the newest fact's claim, else the description. */
  learned: string;
  nodeType: string;
}

interface WallNewItemBase {
  key: WallNewKey;
  /** Epoch milliseconds. */
  createdAt: number;
}

export type WallNewItem =
  | (WallNewItemBase & { kind: "task"; task: WallNewTask })
  | (WallNewItemBase & { kind: "thing"; thing: WallNewThing });

type WallNewTaskItem = Extract<WallNewItem, { kind: "task" }>;
type WallNewThingItem = Extract<WallNewItem, { kind: "thing" }>;

const NONE: readonly WallNewItem[] = [];
/** This week's things fetched for the new cards, newest first. */
const RECENT_THINGS_FETCHED = 50;
const NO_THINGS: readonly WallNewThingItem[] = [];

interface RemoteNewItems {
  /** Everything's tasks, as last fetched (and patched by the card's actions). */
  tasks: readonly WallNewTaskItem[];
  things: readonly WallNewThingItem[];
  /** The key of the fetch the things answer. */
  thingsFor: string | null;
  /** The latest fetch: what it answers, and whether it has settled. */
  fetch: { key: string; settled: boolean } | null;
}

const useRemoteNewItems = create<RemoteNewItems>(() => ({
  tasks: [],
  things: [],
  thingsFor: null,
  fetch: null,
}));

/** A fetch this recent serves a show as it is. */
const FRESH_FETCH_MS = 60_000;

/**
 * What the remote sources answer: the scope's kind, the project on a
 * project wall, and the plan's memory.
 */
function remoteKey(
  scopeKind: WallScope["kind"],
  projectId: string,
  memory: boolean,
): string {
  return scopeKind === "project"
    ? `project:${projectId}:${memory}`
    : `${scopeKind}:${memory}`;
}

/** Whether a scope's new cards come from the server: Everything's tasks, things. */
function fetchesRemote(scopeKind: WallScope["kind"], memory: boolean): boolean {
  return scopeKind !== "archived" && (memory || scopeKind === "all");
}

function fromSearchTask(task: WorkspaceTask): WallNewTaskItem {
  return {
    key: `task:${task.id}`,
    kind: "task",
    createdAt: Date.parse(task.created_at),
    task: {
      id: task.id,
      title: task.title,
      status: task.status,
      ticketDisplay: task.ticket_display,
      assigneeName: task.assignee_name || null,
      projectId: task.project_id,
    },
  };
}

/** Things Denker learned; the user's own and the bookkeeping kinds are not. */
function fromThing(record: KnowledgeNodeListItem): WallNewThingItem[] {
  if (record.origin === "explicit" || !isLearnedThingType(record.node_type)) {
    return [];
  }
  return [
    {
      key: `thing:${record.id}`,
      kind: "thing",
      createdAt: Date.parse(record.created_at),
      thing: {
        id: record.id,
        name: record.name,
        learned: record.latest_claim || record.description,
        nodeType: record.node_type,
      },
    },
  ];
}

/** A status change made from Everything's card, until the next fetch. */
export function patchWallNewTask(taskId: string, status: TaskStatus): void {
  useRemoteNewItems.setState((state) => ({
    tasks: state.tasks.map((item) =>
      item.task.id === taskId
        ? { ...item, task: { ...item.task, status } }
        : item,
    ),
  }));
}

/** A task deleted from Everything's card. */
export function dropWallNewTask(taskId: string): void {
  useRemoteNewItems.setState((state) => ({
    tasks: state.tasks.filter((item) => item.task.id !== taskId),
  }));
}

/** A thing hidden from the wall or forgotten. */
export function dropWallNewThing(thingId: string): void {
  useRemoteNewItems.setState((state) => ({
    things: state.things.filter((item) => item.thing.id !== thingId),
  }));
}

function itemText(item: WallNewItem): string {
  return item.kind === "task"
    ? [item.task.title, item.task.ticketDisplay, item.task.assigneeName].join(
        " ",
      )
    : `${item.thing.name} ${item.thing.learned}`;
}

/**
 * Fetch the remote sources for `key`. Only the latest fetch writes its
 * results; a failed source settles too, and the wall shows without it.
 */
function fetchRemote(
  key: string,
  scopeKind: WallScope["kind"],
  projectId: string,
  memory: boolean,
): void {
  const fetch = { key, settled: false };
  useRemoteNewItems.setState({ fetch });
  const latest = () => useRemoteNewItems.getState().fetch === fetch;
  const createdAfter = new Date(Date.now() - WALL_NEW_MS).toISOString();
  const requests: Promise<void>[] = [];
  if (memory) {
    requests.push(
      listWallThings({
        createdAfter,
        limit: RECENT_THINGS_FETCHED,
        learnedInProject: scopeKind === "project" ? projectId : undefined,
      }).then((records) => {
        if (latest()) {
          useRemoteNewItems.setState({
            things: records.flatMap(fromThing),
            thingsFor: key,
          });
        }
      }),
    );
  }
  if (scopeKind === "all") {
    requests.push(
      searchWorkspace({ query: "", projectId: null, kinds: ["tasks"] }).then(
        (results) => {
          if (latest()) {
            useRemoteNewItems.setState({
              tasks: (results.tasks?.items ?? []).map(fromSearchTask),
            });
          }
        },
      ),
    );
  }
  void Promise.allSettled(requests).then(() => {
    if (latest()) useRemoteNewItems.setState({ fetch: { key, settled: true } });
  });
}

/**
 * Fetch the remote sources once per scope and project ahead of the show, and
 * again on a show when this wall's last fetch is stale. A fetch is never
 * dropped: a show (or StrictMode's second run) joins the one in flight.
 */
function useRemoteFetch(
  visible: boolean,
  scopeKind: WallScope["kind"],
  projectId: string,
  memory: boolean,
): void {
  const lastRef = useRef<{ key: string; startedAt: number } | null>(null);
  useEffect(() => {
    if (!fetchesRemote(scopeKind, memory)) return;
    const key = remoteKey(scopeKind, projectId, memory);
    const last = lastRef.current;
    if (
      last?.key === key &&
      (!visible || Date.now() - last.startedAt < FRESH_FETCH_MS)
    ) {
      return;
    }
    lastRef.current = { key, startedAt: Date.now() };
    fetchRemote(key, scopeKind, projectId, memory);
  }, [memory, projectId, scopeKind, visible]);
}

/**
 * Whether the wall's new cards for `scope` are known: the remote sources'
 * latest fetch has settled, when the scope has any.
 */
export function useWallNewItemsReady(
  projectId: string,
  scope: WallScope,
  memory: boolean,
): boolean {
  const key = remoteKey(scope.kind, projectId, memory);
  const remoteSettled = useRemoteNewItems(
    (state) => state.fetch?.key === key && state.fetch.settled,
  );
  return !fetchesRemote(scope.kind, memory) || remoteSettled;
}

export function useWallNewItems({
  projectId,
  scope,
  query,
  visible,
  memory,
}: {
  projectId: string;
  scope: WallScope;
  query: string;
  /** The wall shows in this window. */
  visible: boolean;
  /** The plan includes the knowledge graph. */
  memory: boolean;
}): readonly WallNewItem[] {
  const scopeKind = scope.kind;
  useRemoteFetch(visible, scopeKind, projectId, memory);
  const key = remoteKey(scopeKind, projectId, memory);
  const remoteTasks = useRemoteNewItems((state) => state.tasks);
  // Only the things this wall's fetch answered: never another project's.
  const things = useRemoteNewItems((state) =>
    state.thingsFor === key ? state.things : NO_THINGS,
  );
  const opened = useWallOpenedKeys();

  return useMemo(() => {
    if (scopeKind === "archived") return NONE;
    const now = Date.now();
    const needle = query.trim().toLowerCase();
    const items = [
      ...(scopeKind === "all" ? remoteTasks : []),
      ...(memory ? things : []),
    ].filter(
      (item) =>
        now - item.createdAt < WALL_NEW_MS &&
        !opened.has(item.key) &&
        (needle === "" || itemText(item).toLowerCase().includes(needle)),
    );
    return items.length > 0 ? items : NONE;
  }, [memory, opened, query, remoteTasks, scopeKind, things]);
}

function frameCreatedAt(frameId: string): number | undefined {
  const at = Date.parse(wallFrame(frameId)?.createdAt ?? "");
  return Number.isNaN(at) ? undefined : at;
}

/**
 * The feed's frame ids with the new cards' keys among them by creation
 * time (`mergeNewItems`), or after a search's ranked frames
 * (`appendNewItems`); the same list until its ids or order change, so an
 * edit to a card's task does not lay the wall out again.
 */
export function useWallFeedWithNewItems(
  frameIds: readonly string[],
  items: readonly WallNewItem[],
  hasMore: boolean,
  /** The frames are a search's ranked page. */
  ranked: boolean,
): readonly string[] {
  const merged = useMemo(
    () =>
      ranked
        ? appendNewItems(frameIds, items)
        : mergeNewItems(frameIds, items, frameCreatedAt, hasMore),
    [frameIds, hasMore, items, ranked],
  );
  const [stable, setStable] = useState(merged);
  if (stable !== merged && !sameIds(stable, merged)) setStable(merged);
  return sameIds(stable, merged) ? stable : merged;
}
